<template>
  <!-- Tutto avvolto da top-bar -->
  <div id="top-bar" class="top-bar">
    <!-- Riga 1: logo + link + logout -->
    <div
      class="top-bar-header"
      :class="{ 'login-layout': $route.path === '/login' }"
    >
      <!-- Logo con link Home -->
      <a
        @click="goToHome"
        role="button"
        class="logo-link"
        :title="haSessione ? 'Clicca per tornare alla home' : ''"
        :aria-label="haSessione ? 'Clicca per tornare alla home' : ''"
      >
        <img
          v-if="showCompanyLogo"
          id="main-logo"
          :src="companyLogoSrc"
          :alt="companyName || 'Logo aziendale'"
          @error="logoUnavailable = true"
        />
        <span v-else-if="companyName" id="main-logo-text">{{
          companyName
        }}</span>
        <img
          v-else
          id="main-logo"
          src="@/assets/logo-mark.png"
          alt="Gestionale"
        />
      </a>

      <!-- Breadcrumb -->
      <div id="breadcrumb-container" v-if="!$route.meta.hideUrlIndex">
        <div id="breadcrumb">
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <router-link
              v-if="index < breadcrumbs.length - 1"
              :to="crumb.path"
              class="breadcrumb-link"
            >
              <i v-if="crumb.name === 'Home'" class="fa-solid fa-house"></i>
              <span v-else>{{ crumb.name }}</span>
            </router-link>
            <span v-if="index < breadcrumbs.length - 1"> / </span>
            <span v-else>
              <i v-if="crumb.name === 'Home'" class="fa-solid fa-house"></i>
              <template v-else>{{ crumb.name }}</template>
            </span>
          </template>
        </div>
      </div>

      <!-- Hamburger toggle (solo mobile) -->
      <button
        v-if="haSessione"
        class="hamburger-toggle"
        @click="toggleMenu"
        aria-label="Apri menu"
      >
        <i :class="menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'"></i>
      </button>

      <!-- Link a destra -->
      <!-- [sc-48497 / task 48551] La condizione è la sessione, non
           l'indirizzo. Prima bastava «non siamo su /login», e da quando esiste
           /registrazione quel controllo lasciava passare Account, Impostazioni
           e soprattutto Logout a chi una sessione non ce l'ha: cliccarlo
           chiamava /auth/logout senza cookie e rispondeva con un errore. -->
      <div class="links" :class="{ open: menuOpen }" v-if="haSessione">
        <!-- Account Link -->
        <button
          v-if="!$route.meta.hideAccountLink && isAdmin && !isPlatformAdmin"
          @click="goToAccount"
          id="account-button"
          class="account-button"
          title="Clicca per andare alla pagina Account"
          aria-label="Clicca per andare alla pagina Account"
        >
          <i class="fa-solid fa-user"></i>
          Account
        </button>

        <!-- Impostazioni: raccoglie le pagine che si aprono di rado e non
             meritano una scheda in home: il marchio e lo scarico dei dati. -->
        <div v-if="isAdmin && !isPlatformAdmin" class="settings-menu">
          <button
            id="settings-button"
            :class="{ open: settingsOpen }"
            @click="toggleSettings"
            :aria-expanded="String(settingsOpen)"
            aria-haspopup="true"
            title="Impostazioni del gestionale"
            aria-label="Impostazioni del gestionale"
          >
            <i class="fa-solid fa-gear"></i>
            Impostazioni
            <i
              class="fa-solid settings-caret"
              :class="settingsOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
          <div v-if="settingsOpen" class="settings-dropdown">
            <router-link
              to="/home/marchio"
              class="settings-item"
              @click="settingsOpen = false"
            >
              <i class="fa-solid fa-palette"></i>
              Configurazione azienda
            </router-link>
            <router-link
              to="/home/dati"
              class="settings-item"
              @click="settingsOpen = false"
            >
              <i class="fa-solid fa-download"></i>
              I tuoi dati
            </router-link>

            <!-- [sc-46314] Le funzioni che esistono solo su CRM PLUS: spente
                 e raccolte sotto il loro titolo, non sparse fra quelle che
                 funzionano. Tre finti pulsanti in mezzo a quelli veri fanno
                 sembrare rotto il gestionale; un gruppo dichiarato si legge
                 come un listino. -->
            <!-- [sc-48351] Un'azienda PLUS il piano ce l'ha già: il gruppo
                 che lo propone non ha niente da dirle. -->
            <template v-if="!isPlus">
              <div class="settings-sep">CRM PLUS - Funzioni avanzate</div>
              <button
                v-for="voce in vociPlus"
                :key="voce.chiave"
                type="button"
                class="settings-item settings-item--plus"
                :title="voce.testo"
                @click="apriPlus"
              >
                <i class="fa-solid" :class="voce.icona" aria-hidden="true"></i>
                {{ voce.etichetta }}
                <i
                  class="fa-solid fa-lock settings-lock"
                  aria-hidden="true"
                ></i>
              </button>
            </template>
          </div>
        </div>

        <!-- Guida (regola #20) -->
        <!-- [sc-46314] Due modi di farsi spiegare le cose - il manuale e il
             giro guidato - stanno sotto lo stesso pulsante. Qui e non in
             Impostazioni perché quel menù lo vedono solo gli amministratori,
             mentre il tutorial lo fa ogni persona che entra.

             L'amministratore di piattaforma è l'eccezione: nel gestionale dei
             clienti non entra, legge numeri e attiva aziende. Un manuale
             operativo e un giro guidato che non farà mai gli direbbero solo
             che è nel posto sbagliato. -->
        <div v-if="!isPlatformAdmin" class="guide-menu">
          <button
            id="guide-button"
            :class="{ open: guideOpen }"
            @click="toggleGuide"
            :aria-expanded="String(guideOpen)"
            aria-haspopup="true"
            title="Manuale e tutorial"
            aria-label="Manuale e tutorial"
          >
            <i class="fa-solid fa-circle-question"></i>
            {{ $t("help") || "Aiuto" }}
            <i
              class="fa-solid settings-caret"
              :class="guideOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
          <div v-if="guideOpen" class="settings-dropdown">
            <button
              type="button"
              class="settings-item settings-item--azione"
              @click="openGuide"
            >
              <i class="fa-solid fa-book-open" aria-hidden="true"></i>
              Manuale operativo
            </button>
            <!-- [sc-46314] Due bisogni diversi: «cos'era questo campo?» si
                 risolve sul posto, senza portare via da un modulo mezzo
                 compilato - su nuovo cliente, articolo e preventivo la
                 navigazione fa scattare la guardia delle modifiche non
                 salvate. -->
            <button
              v-if="paginaSpiegabile"
              type="button"
              class="settings-item settings-item--azione"
              @click="spiegaPagina"
            >
              <i class="fa-solid fa-lightbulb" aria-hidden="true"></i>
              Spiegami questa pagina
            </button>
            <!-- «Salta» non deve essere una porta a senso unico. -->
            <button
              type="button"
              class="settings-item settings-item--azione"
              @click="rivediTutorial"
            >
              <i class="fa-solid fa-graduation-cap" aria-hidden="true"></i>
              Rivedi il tutorial
            </button>
          </div>
        </div>

        <!-- Logout Link -->
        <button
          v-if="!$route.meta.hideLogout"
          id="logout-button"
          @click="logout"
          :disabled="isLoggingOut"
          title="Clicca per effettuare il logout"
          aria-label="Clicca per effettuare il logout"
        >
          <i
            :class="
              isLoggingOut
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-right-from-bracket'
            "
          ></i>
          {{ isLoggingOut ? "Uscita…" : "Logout" }}
        </button>
      </div>

      <PlusCallModal :aperta="plusAperta" @chiudi="plusAperta = false" />

      <!-- Overlay per chiudere i dropdown -->
      <div
        v-if="menuOpen || settingsOpen || guideOpen"
        class="menu-overlay"
        @click="closeMenus"
      ></div>
    </div>
  </div>
</template>

<script>
import { setAuth } from "../router";
import { trackEvent, EVENTS } from "@/utilities/analytics";
import { categoryLabel } from "@/config/listiniCategories";
import { companyLogoUrl } from "@/config/branding";
import PlusCallModal from "@/components/PlusCallModal.vue";
import { VOCI_PLUS } from "@/config/onboarding";
import { haSpiegazione } from "@/utilities/onboarding";
// Alias: il nome `isPlatformAdmin` qui è già preso dal computed che segue.
import { isPlatformAdmin as riconosceAdminDiPiattaforma } from "@/utilities/platformGate";

export default {
  name: "TopBar",
  components: { PlusCallModal },
  data() {
    return {
      breadcrumbs: [],
      menuOpen: false,
      settingsOpen: false,
      guideOpen: false,
      plusAperta: false,
      isLoggingOut: false,
      // Il profilo dice che un logo c'è, ma il file potrebbe non arrivare
      // (storage non raggiungibile): in quel caso si ripiega sul nome.
      logoUnavailable: false,
    };
  },
  computed: {
    /** C'è qualcosa da rispiegare sulla pagina in cui siamo? */
    paginaSpiegabile() {
      return haSpiegazione(this.$route.name);
    },
    /** Le voci che esistono solo su PLUS, dal registro del tutorial. */
    vociPlus() {
      return VOCI_PLUS;
    },
    /** [sc-48351] Il piano dell'azienda collegata, dal profilo. */
    isPlus() {
      return this.$store.getters["company/plan"] === "PLUS";
    },
    isAdmin() {
      return this.$store.state.currentUser?.isAdmin ?? false;
    },
    /**
     * C'è qualcuno collegato?
     *
     * [sc-48497 / task 48551] Lo dice lo store, che il cancello del router
     * riempie leggendo il profilo e svuota all'uscita. È l'unica domanda
     * sensata da fare qui: la barra offre azioni che senza sessione non
     * possono riuscire, e l'elenco delle pagine dove non c'è una sessione si
     * allunga a ogni pagina pubblica che aggiungiamo.
     */
    haSessione() {
      return Boolean(this.$store.state.currentUser);
    },
    /**
     * È l'amministratore di crmstart?
     *
     * [sc-48497 / task 48534] Account e Impostazioni parlano dell'azienda di
     * chi guarda, e lui un'azienda non ce l'ha: dalla 48534 quelle rotte gli
     * rispondono 403. Nascondere i pulsanti è dire la stessa cosa prima, e
     * senza un errore rosso.
     */
    isPlatformAdmin() {
      return riconosceAdminDiPiattaforma(this.$store.state.currentUser);
    },
    branding() {
      return this.$store.getters["company/branding"];
    },
    companyName() {
      return this.branding?.name || "";
    },
    showCompanyLogo() {
      return Boolean(this.branding?.hasLogo) && !this.logoUnavailable;
    },
    companyLogoSrc() {
      // La versione fa parte dell'indirizzo: cambia quando il logo cambia, e
      // il browser va a riprenderlo invece di riusare quello in cache.
      return companyLogoUrl(undefined, this.branding?.logoVersion);
    },
  },
  watch: {
    "$route.path": {
      immediate: true,
      handler() {
        this.breadcrumbs = this.generateBreadcrumbs();
        this.menuOpen = false;
        this.settingsOpen = false;
        this.guideOpen = false;
        this.isLoggingOut = false;
      },
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.chiudiSeCliccoFuori);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.chiudiSeCliccoFuori);
  },
  methods: {
    /**
     * Le tendine si chiudono anche cliccando fuori.
     *
     * [sc-46314, review 48629] C'era gia' `.menu-overlay`, ma il foglio di
     * stile lo tiene a `display: none` sopra i 1024 pixel: da desktop
     * Impostazioni e Aiuto restavano aperte finche' non si ricliccava il
     * pulsante. Un ascoltatore invece dell'overlay anche sul grande schermo
     * perche' l'overlay si mangia il primo clic, e chi voleva premere un
     * pulsante dietro la tendina dovrebbe premerlo due volte.
     */
    chiudiSeCliccoFuori(evento) {
      if (!this.settingsOpen && !this.guideOpen) return;
      const dentro = evento.target?.closest?.(".settings-menu, .guide-menu");
      if (dentro) return;
      this.settingsOpen = false;
      this.guideOpen = false;
    },

    generateBreadcrumbs() {
      const params = this.$route.params;
      return this.$route.matched.map((matchedRoute) => {
        let path = matchedRoute.path;
        for (const [key, value] of Object.entries(params)) {
          path = path.replace(`:${key}`, value);
        }
        let name = matchedRoute.meta.breadcrumb || matchedRoute.name;
        if (matchedRoute.name === "listini-category" && params.category) {
          // Nel percorso l'utente legge l'etichetta della scheda, non lo slug
          // tecnico usato dalla rotta. [sc-48583 / task 48599] L'etichetta è
          // quella della categoria scelta in configurazione, con ripiego su
          // «Listino» per chi non ha ancora scelto.
          name = categoryLabel(
            params.category,
            this.$store.getters["company/branding"]?.productCategories
          );
        }
        return { name, path };
      });
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      this.settingsOpen = false;
    },

    toggleSettings() {
      this.settingsOpen = !this.settingsOpen;
      this.guideOpen = false;
    },

    toggleGuide() {
      this.guideOpen = !this.guideOpen;
      this.settingsOpen = false;
    },

    /**
     * Rimette il giro guidato da capo e riporta in home.
     *
     * In home perché è l'unico posto in cui il benvenuto si apre: azzerarlo
     * standando in Magazzino non mostrerebbe niente, e sembrerebbe un
     * pulsante rotto.
     */
    /**
     * Rimostra pannellino e riflettore della pagina in cui siamo.
     *
     * Senza navigare: chi lo chiede è già dove vuole essere.
     */
    spiegaPagina() {
      this.guideOpen = false;
      return this.$store.dispatch("spiegaQuestaPagina", this.$route.name);
    },

    rivediTutorial() {
      this.guideOpen = false;
      this.$store.dispatch("riapriTutorial");
      return this.$router.push({ name: "home" });
    },

    /**
     * Apre il riquadro di CRM PLUS.
     *
     * Il menù si chiude prima: lasciarlo aperto sotto la finestra la fa
     * sembrare incastrata, e all'`Esc` risponderebbero in due.
     */
    apriPlus() {
      this.settingsOpen = false;
      this.plusAperta = true;
    },

    closeMenus() {
      this.menuOpen = false;
      this.settingsOpen = false;
      this.guideOpen = false;
    },

    goToAccount() {
      this.menuOpen = false;
      if (this.haSessione) {
        this.$router.push("/home/account");
      }
      return;
    },

    openGuide() {
      // Solo il nome tecnico della funzione usata.
      trackEvent(EVENTS.FEATURE_USED, { feature: "guide", action: "open" });
      this.menuOpen = false;
      this.guideOpen = false;
      const locale = this.$i18n?.locale === "en" ? "en" : "it";
      const file = locale === "en" ? "manuale_en.html" : "manuale.html";
      const base = process.env.BASE_URL || "/";
      window.open(`${base}guide/${file}`, "_blank", "noopener");
    },

    goToHome() {
      if (!this.haSessione) return;
      // Ognuno alla propria casa: per l'amministratore di crmstart la home è
      // la console, il gestionale non è suo.
      this.$router.push(this.isPlatformAdmin ? "/piattaforma" : "/home");
    },

    async logout() {
      this.menuOpen = false;

      // Senza sessione non c'è niente da chiudere, e la richiesta tornerebbe
      // con un errore che non racconta niente a nessuno.
      if (!this.haSessione) {
        return;
      }

      this.isLoggingOut = true;
      try {
        const response = await this.$axios.post("/auth/logout");
        if (response.status === 200) {
          setAuth(false);
          // [sc-48497 / task 48522] Il marchio nello store è ancora quello
          // dell'azienda da cui si è appena usciti. Senza questa richiesta la
          // pagina di accesso resta vestita da lei fino a un ricaricamento, e
          // sul computer di chi entra dopo comparirebbe il marchio di un
          // cliente che non è il suo. Senza sessione la rotta risponde col
          // marchio di crmstart.
          await this.$store.dispatch("fetchCompanyBranding");
          await this.$router.push("/login");
          this.$toast.success("Logout effettuato con successo!");
        }
      } catch (error) {
        this.isLoggingOut = false;
        this.errorMessage = "Errore durante il logout";
        this.$toast.error(this.errorMessage);
        setTimeout(() => {
          this.errorMessage = "";
        }, 2000);
      }
    },
  },
};
</script>

<style src="@/css/App.css"></style>
